import React, { useEffect, useState } from "react";
import { MousePointerClick, RefreshCw, BadgeCheck } from "lucide-react";
import { api } from "@/lib/api";
import LoadErrorBanner, { describeLoadError } from "@/components/admin/LoadErrorBanner";
import Sparkline from "@/components/Sparkline";

const fmt = (iso) => { const d = new Date(iso || ""); return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString("en-AU", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }); };

// Admin → Founding Suppliers: homepage Fresh Picks click counts per card, for reporting featured traffic to suppliers.
export default function SpotlightClicksTable() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    setBusy(true); setError(null);
    try { const { data: d } = await api.get("/admin/spotlight-clicks"); setData(d); }
    catch (e) { setError(describeLoadError(e, "Fresh Picks clicks")); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);
  const items = data?.items || [];
  return (
    <div className="bg-white border border-[#E8ECE9] p-5" data-testid="spotlight-clicks">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
        <div>
          <div className="font-serif text-lg text-[#11211A] flex items-center gap-2"><MousePointerClick className="w-4 h-4 text-[#264839]" /> Fresh Picks clicks</div>
          <p className="text-xs text-[#7D8C85] mt-0.5">Every click on a homepage "Fresh picks" card, counted per listing — share these numbers with featured suppliers like Setuply.</p>
        </div>
        <div className="flex items-center gap-3 text-xs text-[#7D8C85]">
          <span data-testid="spotlight-clicks-total">{data ? `${data.total_clicks} click${data.total_clicks === 1 ? "" : "s"} all-time` : "…"}</span>
          <button type="button" onClick={load} className="btn-ghost text-xs" data-testid="spotlight-clicks-refresh"><RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} /></button>
        </div>
      </div>
      <LoadErrorBanner error={error} onRetry={load} testId="spotlight-clicks-error" />
      {data && items.length === 0 && <div className="text-xs text-[#7D8C85]" data-testid="spotlight-clicks-empty">No Fresh Picks clicks recorded yet.</div>}
      {items.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-[11px] uppercase tracking-wider text-[#7D8C85] border-b border-[#E8ECE9]">
              <th className="py-2 pr-3 font-medium">Listing</th><th className="py-2 px-3 font-medium">Supplier</th>
              <th className="py-2 px-3 font-medium">Last 30 days</th><th className="py-2 px-3 font-medium text-right">Mobile</th><th className="py-2 px-3 font-medium text-right">Desktop</th><th className="py-2 px-3 font-medium text-right">7 days</th><th className="py-2 px-3 font-medium text-right">30 days</th><th className="py-2 px-3 font-medium text-right">All-time</th><th className="py-2 pl-3 font-medium">Last click</th>
            </tr></thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.listing_id} className="border-b border-[#F0EFEA]" data-testid={`spotlight-row-${r.listing_id}`}>
                  <td className="py-2 pr-3">
                    <div className="flex items-center gap-2">
                      <span className="text-[#11211A]">{r.title}</span>
                      {r.founding_supplier && r.is_verified && <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider bg-[#264839] text-white px-1.5 py-0.5"><BadgeCheck className="w-3 h-3" /> Verified &amp; Featured</span>}
                      {!r.in_rotation && <span className="text-[9px] uppercase tracking-wider text-[#7D8C85]">not in rotation</span>}
                    </div>
                  </td>
                  <td className="py-2 px-3 text-[#4A5550]">{r.company}{r.supplier_email && <div className="text-[11px] text-[#7D8C85]">{r.supplier_email}</div>}</td>
                  <td className="py-2 px-3"><Sparkline series={r.series_30d} testId={`spotlight-sparkline-${r.listing_id}`} /></td>
                  <td className="py-2 px-3 text-right tabular-nums" data-testid={`spotlight-mobile-${r.listing_id}`}>{r.mobile}</td>
                  <td className="py-2 px-3 text-right tabular-nums" data-testid={`spotlight-desktop-${r.listing_id}`}>{r.desktop}</td>
                  <td className="py-2 px-3 text-right tabular-nums" data-testid={`spotlight-7d-${r.listing_id}`}>{r.last_7d}</td>
                  <td className="py-2 px-3 text-right tabular-nums" data-testid={`spotlight-30d-${r.listing_id}`}>{r.last_30d}</td>
                  <td className="py-2 px-3 text-right tabular-nums font-medium text-[#11211A]" data-testid={`spotlight-total-${r.listing_id}`}>{r.total}</td>
                  <td className="py-2 pl-3 text-[11px] text-[#7D8C85] whitespace-nowrap">{fmt(r.last_click_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
